<template>
  <b-tabs v-model="activeMainTab" class="mb-2">
    <b-tab-item :label="$t('integrations.webhookBuilder')" :value="'form'">
      <!-- Parameters slot just below status -->
      <slot name="parameters"
        :parameters="form.parameters"
        :secrets="form.secrets"
        :get-secret="getSecret"
        :set-secret="setSecret"
        :get-param="getParam"
        :set-param="setParam"
        :set-parameter-value="(idx, val) => (form.parameters[idx].value = val)">
        <div class="box" v-if="form.parameters && form.parameters.length">
          <h2 class="subtitle">{{ $t('integrations.parameters') }}</h2>
          <div class="columns is-mobile" v-for="(param, idx) in form.parameters" :key="param.template_name || idx">
            <div class="column">
              <b-field :label="param.template_name || ('Param ' + (idx + 1))">
                <b-input v-model="param.value"></b-input>
              </b-field>
            </div>
          </div>
        </div>
      </slot>

      <div class="box">
        <h2 class="subtitle">{{ $t('integrations.generalSettings') }}</h2>

        <BFieldHint :label="$t('integrations.name')" :hintKey="$t('integrations.nameHelp')">
          <b-input ref="nameInput" v-model="form.name" :placeholder="$t('integrations.namePlaceholder')" required maxlength="64" pattern="[a-Z/_0-9]*" :validation-message="$t('integrations.nameValidationError')"/>
        </BFieldHint>

        <BFieldHint :label="$t('integrations.device')" hintKey="integrations.deviceHelp">
          <b-select v-model="form.deviceId">
            <option :value="'*'">{{ $t('integrations.anyDevice') }}</option>
            <option v-for="d in deviceList" :key="d.id" :value="d.id">{{ d.name || d.id }}</option>
          </b-select>
        </BFieldHint>

        <BFieldHint :label="$t('integrations.status')" hintKey="integrations.statusHelp">
          <b-switch v-model="form.enabled">{{ form.enabled ? $t('integrations.enabled') : $t('integrations.disabled') }}</b-switch>
        </BFieldHint>

        <BFieldHint :label="$t('global.secrets')" hintKey="integrations.secretsHelp" v-if="isVisible('secrets')">
          <b-taginput
            v-model="selectedSecrets"
            :data="filteredSecretOptions"
            autocomplete
            :open-on-focus="true"
            :allow-new="false"
            :placeholder="$t('secrets.selectSecretsPlaceholder')"
            type="is-primary"
            @typing="onSecretTyping"
          />
          <div class="mt-2">
            <b-button type="is-primary" @click="secretModalOpen = true">{{ $t('secrets.addNewButton') }}</b-button>
          </div>
        </BFieldHint>

      </div>

      <div class="box" v-if="isVisible('requestURL') || isVisible('requestType') || isVisible('requestFormat')">
        <h2 class="subtitle">{{ $t('integrations.webhookRequest') }}</h2>

        <b-field :label="$t('integrations.url')" v-if="isVisible('requestURL')">
          <b-input ref="urlInput" v-model="form.url" required :placeholder="$t('integrations.urlPlaceholder')" type="url"></b-input>
        </b-field>

        <BFieldHint v-if="isVisible('requestType')" :label="$t('integrations.requestType')" hintKey="integrations.requestTypeHelp">
          <b-select v-model="form.requestType">
            <option value="POST">POST</option>
            <option value="GET">GET</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </b-select>
        </BFieldHint>

        <div v-if="form.requestType !== 'GET' && isVisible('requestFormat')">
          <BFieldHint :label="$t('integrations.requestFormat')" hintKey="integrations.requestFormatHelp">
            <b-tabs type="is-toggle" v-model="activeRequestFormatTab">
              <b-tab-item :label="$t('integrations.requestFormatForm')" :value="'form'" :disabled="form.requestType === 'GET' || !isVisible('requestFormatForm')">
                <BFieldHint :label="$t('integrations.webForm')" hintKey="integrations.webFormHelp">
                  <div class="mb-3">
                    <b-field>
                      <b-radio-button size="is-small" v-model="defaultsSelection" native-value="default">{{ $t('integrations.default') }}</b-radio-button>
                      <b-radio-button size="is-small" v-model="defaultsSelection" native-value="custom">{{ $t('integrations.custom') }}</b-radio-button>
                    </b-field>
                  </div>
                  <div v-if="!form.noDefaults">
                    <KeyValueList :modelValue="defaultFormPairs" :readOnly="true" />
                  </div>
                  <KeyValueList v-model="form.formParams" :hideHeader="!form.noDefaults" />
                </BFieldHint>
              </b-tab-item>
              <b-tab-item :label="$t('integrations.requestFormatJson')" :value="'json'" :disabled="form.requestType === 'GET' || !isVisible('requestFormatJson')">
                <BFieldHint :label="$t('integrations.jsonBody')" hintKey="integrations.jsonBodyHelp">
                  <div class="mb-3" v-if="isVisible('requestFormatJsonDefault')">
                    <b-field>
                      <b-radio-button size="is-small" v-model="defaultsSelection" native-value="default">{{ $t('integrations.default') }}</b-radio-button>
                      <b-radio-button size="is-small" v-model="defaultsSelection" native-value="custom">{{ $t('integrations.custom') }}</b-radio-button>
                    </b-field>
                  </div>
                  <div class="fermi-json-editor">
                    <div v-if="!form.noDefaults">
                      <JsonEditorVue
                        :modelValue="defaultJsonObject"
                        :mode="jsonEditorMode"
                        :stringified="false"
                        :main-menu-bar="false"
                        :navigation-bar="false"
                        :status-bar="false"
                        :readOnly="true"
                        style="height: 320px;"
                      />
                    </div>
                    <div v-else>
                      <JsonEditorVue
                        v-model="form.jsonData"
                        :mode="jsonEditorMode"
                        :stringified="false"
                        :main-menu-bar="false"
                        :navigation-bar="false"
                        :status-bar="false"
                        style="height: 320px;"
                      />
                    </div>
                  </div>
                </BFieldHint>
              </b-tab-item>
              <b-tab-item :label="$t('integrations.requestFormatCustom')" :value="'custom'" :disabled="form.requestType === 'GET' || !isVisible('requestFormatCustom')">
                <BFieldHint :label="$t('integrations.customBody')" hintKey="integrations.customBodyHelp">
                  <b-input type="textarea" rows="10" v-model="form.body" :placeholder="$t('integrations.customBodyPlaceholder')" />
                </BFieldHint>
              </b-tab-item>
            </b-tabs>
          </BFieldHint>

          <hr v-if="isVisible('queryParams')">

        </div>

        <BFieldHint v-if="isVisible('queryParams')" :label="$t('integrations.queryParams')" hintKey="integrations.queryParamsHelp">
          <div v-if="form.requestType === 'GET'" class="mb-3">
            <b-field>
              <b-radio-button size="is-small" v-model="defaultsSelection" native-value="default">{{ $t('integrations.default') }}</b-radio-button>
              <b-radio-button size="is-small" v-model="defaultsSelection" native-value="custom">{{ $t('integrations.custom') }}</b-radio-button>
            </b-field>
          </div>

          <div v-if="form.requestType === 'GET' && !form.noDefaults">
            <KeyValueList :modelValue="defaultFormPairs" :readOnly="true" />
          </div>
          <KeyValueList v-model="form.queryParams" :hideHeader="form.requestType === 'GET' && !form.noDefaults" />
        </BFieldHint>

        <hr v-if="isVisible('httpHeaders')">

        <BFieldHint v-if="isVisible('httpHeaders')" :label="$t('integrations.httpHeaders')" hintKey="integrations.httpHeadersHelp">
          <div v-if="form.requestFormat === 'json'" class="mb-3">
            <KeyValueList :modelValue="defaultJsonHeaders" :readOnly="true" />
          </div>
          <div v-else-if="form.requestFormat === 'form'" class="mb-3">
            <KeyValueList :modelValue="defaultFormHeaders" :readOnly="true" />
          </div>
          <KeyValueList v-model="form.headers" :hideHeader="form.requestFormat === 'json' || form.requestFormat === 'form'" />
        </BFieldHint>

        <hr v-if="isVisible('httpBasicAuth')">

        <BFieldHint v-if="isVisible('httpBasicAuth')" :label="$t('integrations.httpBasicAuth')" hintKey="integrations.httpBasicAuthHelp">
        <div class="columns is-mobile">
          <div class="column">
            <b-field :label="$t('integrations.username')">
              <b-input v-model="form.basicAuth.username"></b-input>
            </b-field>
          </div>
          <div class="column">
            <b-field :label="$t('integrations.password')">
              <b-input type="password" password-reveal v-model="form.basicAuth.password"></b-input>
            </b-field>
          </div>
        </div>
        </BFieldHint>

      </div>

      <div class="box" v-if="isVisible('responseTemplate')">
        <h2 class="subtitle">{{ $t('integrations.webhookResponse') }}</h2>

        <BFieldHint v-if="isVisible('responseTemplate')" :label="$t('integrations.responseTemplate')" hintKey="integrations.responseTemplateHelp">
          <b-input type="textarea" rows="6" v-model="form.responseTemplate" :placeholder="$t('integrations.responseTemplatePlaceholder')"></b-input>
        </BFieldHint>

      </div>

    </b-tab-item>
    <b-tab-item v-if="isVisible('jsonTab')" :label="$t('integrations.jsonTemplate')" :value="'json'">
      <div class="box">
        <h2 class="subtitle">{{ $t('integrations.integrationJsonRepresentation') }}</h2>
        <div class="fermi-json-editor">
          <JsonEditorVue
            v-model="integrationInitial"
            :mode="jsonEditorMode"
            :stringified="false"
            :main-menu-bar="false"
            :navigation-bar="false"
            :status-bar="false"
            style="height: 520px;"
          />
        </div>
      </div>
    </b-tab-item>
  </b-tabs>

  <b-modal v-model="secretModalOpen" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" :can-cancel="['escape','x','outside']" aria-label="Add Secret" close-button-aria-label="Close" aria-modal>
    <template #default="props">
      <div class="modal-card" style="width: 600px; max-width: 95vw;">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('secrets.addNew') }}</p>
          <button class="delete" aria-label="close" @click="props.close"></button>
        </header>
        <section class="modal-card-body">
          <SecretForm :rows="4" ref="secretFormInModal" @saved="handleSecretCreated" @valid="v => (canSaveSecret = v)" />
        </section>
        <footer class="modal-card-foot">
          <b-button type="is-primary" :disabled="!canSaveSecret" @click="secretFormInModal?.submit?.()">{{ $t('global.save') }}</b-button>
          <b-button @click="props.close">{{ $t('global.close') }}</b-button>
        </footer>
      </div>
    </template>
  </b-modal>

  <!-- Missing something? section -->
  <div v-if="!isAdvancedView" class="box">
    <div class="content has-text-centered">
      <p class="mb-4 is-info">{{ $t('integrations.missingSomething') }}</p>
      <b-button type="is-info is-outlined" @click="toggleView">
        <b-icon icon="cogs" size="is-small" />
        <span>{{ $t('integrations.showAdvancedSettings') }}</span>
      </b-button>
    </div>
  </div>

  <div>
    <b-button type="is-primary" @click="save" :loading="saving" :disabled="!isValid">{{ $t('global.save') }}</b-button>
    <b-button class="ml-2" @click="$router.back()">{{ $t('global.back') }}</b-button>
  </div>

</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed, getCurrentInstance, watch, PropType, onMounted } from 'vue'
import { Mode } from 'vanilla-jsoneditor'

import { useStore } from 'vuex'
import KeyValueList from '@/components/KeyValueList.vue'
import JsonEditorVue from 'json-editor-vue'
import BFieldHint from '@/components/BFieldHint.vue'
import SecretForm from '@/components/SecretForm.vue'

export default defineComponent({
  name: 'WebhookForm',
  components: { KeyValueList, JsonEditorVue, BFieldHint, SecretForm },
  props: {
    id: String,
    initial: Object as PropType<Record<string, unknown>>,
    visibilityConfig: Object as PropType<Record<string, boolean>>
  },
  emits: ['saved'],
  setup(props, { emit }) {
    const store = useStore()
    const vm = getCurrentInstance()
    type DeviceSummary = { id: string; name?: string }
    type I18nProxy = { $t?: (key: string) => string }
    type IntegrationInitial = {
      name?: string
      template?: string
      deviceID?: string
      url?: string
      requestType?: string
      form?: Record<string, unknown>
      json?: Record<string, unknown>
      body?: string
      query?: Record<string, unknown>
      headers?: Record<string, unknown>
      auth?: { username?: string; password?: string }
      disabled?: boolean
      noDefaults?: boolean
      responseTemplate?: string
      parameters?: Array<{ template_name?: string; value?: string }>
      secrets?: Array<{ template_name?: string; name?: string }>
    }

    const form = reactive({
      name: '',
      url: '',
      requestType: 'POST',
      requestFormat: 'json',
      deviceId: '*',
      enabled: true,
      noDefaults: false,
      body: '',
      queryParams: [{ key: '', value: '' }] as Array<{ key: string; value: string }>,
      formParams: [{ key: '', value: '' }] as Array<{ key: string; value: string }>,
      jsonText: '' as string,
      jsonData: {} as Record<string, unknown>,
      parameters: [] as Array<{ template_name: string; value: string }>,
      secrets: [] as Array<{ template_name?: string; name?: string }>,
      headers: [{ key: '', value: '' }] as Array<{ key: string; value: string }>,
      basicAuth: { username: '', password: '' } as { username: string; password: string },
      responseTemplate: '',
      template: 'webhook'
    })

    // Secrets selection
    const secretModalOpen = ref(false)
    const secretFormInModal = ref<any>(null)
    const canSaveSecret = ref(false)
    const secretQuery = ref('')
    const selectedSecrets = ref<string[]>([])
    const secretOptions = computed<string[]>(() => {
      const s: any = store.state.secrets || []
      const list = Array.isArray(s) ? s : Object.values(s)
      return list.map((x: any) => String(x.name)).filter(Boolean)
    })
    const filteredSecretOptions = computed(() => {
      if (!secretQuery.value) return secretOptions.value
      const q = secretQuery.value.toUpperCase()
      return secretOptions.value.filter(n => n.toUpperCase().includes(q))
    })
    const onSecretTyping = (val: string) => { secretQuery.value = val || '' }
    const handleSecretCreated = async (newName?: string) => {
      if (newName && !selectedSecrets.value.includes(newName)) selectedSecrets.value.push(newName)
      await store.dispatch('loadModel', { model: 'secrets', force: true })
      secretModalOpen.value = false
    }

    // request format selection handled via tabs for non-GET

    watch(() => form.requestType, (newType) => {
      if (newType === 'GET') {
        form.requestFormat = 'query'
        activeRequestFormatTab.value = null
      } else if (form.requestFormat === 'query') {
        form.requestFormat = 'json'
        activeRequestFormatTab.value = 'json'
      }
    })

    // Validation refs and helpers (mirror SecretForm.vue approach)
    const nameInput = ref<any>(null)
    const urlInput = ref<any>(null)
    type NativeField = HTMLInputElement | HTMLTextAreaElement
    const getNative = (cmp: any): NativeField | null => {
      if (!cmp) return null
      return (
        (cmp.$refs && (cmp.$refs.input as NativeField)) ||
        (cmp.$refs && (cmp.$refs.textarea as NativeField)) ||
        (cmp.$el?.querySelector?.('input, textarea') as NativeField | null) ||
        null
      )
    }
    const check = (cmp: any): boolean => {
      const el = getNative(cmp)
      return !!el && el.checkValidity()
    }
    const nameIsValid = computed(() => { void form.name; return check(nameInput.value) })
    const urlIsValid = computed(() => { void form.url; return check(urlInput.value) })
    const isValid = computed(() => true) // nameIsValid.value && urlIsValid.value)

    // hydrate from initial when editing
    watch(() => props.initial, (val) => {
      if (!val) return
      // map common fields
      const v = val as IntegrationInitial
      form.name = (v.name as string) || ''
      form.url = (v.url as string) || ''
      form.requestType = (v.requestType as string) || 'POST'
      // device selection (deviceID present => specific device)
      const initialDeviceId = v.deviceID as string | undefined
      form.deviceId = initialDeviceId && String(initialDeviceId).length > 0 ? String(initialDeviceId) : '*'
      // enabled/disabled (prefer disabled flag, fallback to enabled=false)
      if (typeof v.disabled === 'boolean') {
        form.enabled = !v.disabled
      } else {
        form.enabled = true
      }
      // infer requestFormat
      if (form.requestType === 'GET') {
        form.requestFormat = 'query'
      } else if (v.form) {
        form.requestFormat = 'form'
      } else if (v.json) {
        form.requestFormat = 'json'
      } else if (v.body) {
        form.requestFormat = 'custom'
      } else {
        form.requestFormat = 'json'
      }
      // map query object to array
      const q: Record<string, unknown> = v.query ?? {}
      const entries = Object.keys(q).map(k => ({ key: k, value: String((q as Record<string, unknown>)[k] ?? '') }))
      form.queryParams = entries.length ? entries : [{ key: '', value: '' }]
      const f: Record<string, unknown> = v.form ?? {}
      const fEntries = Object.keys(f).map(k => ({ key: k, value: String((f as Record<string, unknown>)[k] ?? '') }))
      form.formParams = fEntries.length ? fEntries : [{ key: '', value: '' }]
      const j = v.json
      form.jsonText = j ? JSON.stringify(j, null, 2) : ''
      form.jsonData = j || {}
      form.noDefaults = v.noDefaults === true
      form.body = (v.body as string) || ''
      const h: Record<string, unknown> = v.headers ?? {}
      const hEntries = Object.keys(h).map(k => ({ key: k, value: String((h as Record<string, unknown>)[k] ?? '') }))
      form.headers = hEntries.length ? hEntries : [{ key: '', value: '' }]
      // parameters
      const paramsArr: any[] = v.parameters || []
      form.parameters = Array.isArray(paramsArr)
        ? paramsArr.map((p: any) => ({ template_name: String(p?.template_name || ''), value: String(p?.value || '') })).filter(p => p.template_name)
        : []
      // responses
      form.responseTemplate = (v.responseTemplate as string) || ''
      // basic auth
      const au: any = v.auth || {}
      form.basicAuth.username = au.username || ''
      form.basicAuth.password = au.password || ''
      // basic auth not in examples
      // secrets
      const secretsArr: any[] = v.secrets || []
      form.secrets = Array.isArray(secretsArr)
        ? secretsArr.map((s: any) => ({ template_name: s?.template_name, name: s?.name }))
        : []
      selectedSecrets.value = form.secrets.map((s: any) => String(s?.name || '')).filter(Boolean)
      form.template = v.template || 'webhook'
    }, { immediate: true })

    const saving = ref(false)
    const deviceList = computed<DeviceSummary[]>(() => Object.values(store.state.devices) as DeviceSummary[])

    type ToastProxy = { $buefy?: { toast?: { open?: (opts: { message: string; type: string }) => void } } }
    const openToast = (message: string, type: string) => {
      const proxy = vm?.proxy as unknown as ToastProxy
      proxy?.$buefy?.toast?.open?.({ message, type })
    }

    const save = async () => {
      saving.value = true
      try {
        // Build content according to examples / Particle webhook format
        const queryObj = form.queryParams
          .filter(p => p.key || p.value)
          .reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {} as Record<string, string>)
        const content: Record<string, unknown> = {
          name: form.name,
          url: form.url,
          requestType: form.requestType,
          responseTemplate: form.responseTemplate,
        }
        if (form.deviceId && form.deviceId !== '*') {
          content.deviceID = form.deviceId
        }
        // disabled flag only when disabled
        if (!form.enabled) {
          content.disabled = true
        }
        if (form.noDefaults) {
          content.noDefaults = true
        }
        if (form.secrets && form.secrets.length > 0) {
          (content as any).secrets = form.secrets
        } else if (selectedSecrets.value.length > 0) {
          (content as any).secrets = selectedSecrets.value.map(n => ({ name: n }))
        }
        // headers
        const headerObj = form.headers
          .filter(p => p.key || p.value)
          .reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {} as Record<string, string>)
        if (Object.keys(headerObj).length > 0) {
          (content as any).headers = headerObj
        }
        // parameters
        if (form.parameters && form.parameters.length > 0) {
          (content as any).parameters = form.parameters.map(p => ({ template_name: p.template_name, value: p.value }))
        }
        // auth
        if (form.basicAuth.username || form.basicAuth.password) {
          (content as any).auth = {
            username: form.basicAuth.username || '',
            password: form.basicAuth.password || ''
          }
        }
        if (form.requestType === 'GET') {
          content.query = queryObj
        }
        if (form.requestFormat === 'form') {
          const formObj = form.formParams.filter(p => p.key || p.value).reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {} as Record<string, string>)
          content.form = formObj
        } else if (form.requestFormat === 'json') {
          content.json = form.jsonData || {}
        } else if (form.requestFormat === 'custom') {
          if (form.body) content.body = form.body
        }
        // for non-GET, include query if any parameters were provided
        if (form.requestType !== 'GET' && Object.keys(queryObj).length > 0) {
          content.query = queryObj
        }
        if (form.template !== 'webhook') {
          content.template = form.template
        }

        // Determine id: create if none
        const targetId = props.id || crypto.randomUUID()

        await store.dispatch('upsertIntegration', { id: targetId, body: content })
        const i18n = vm?.proxy as unknown as I18nProxy
        openToast((i18n?.$t && i18n.$t('integrations.toastSaved')), 'is-success')
        emit('saved')
      } catch (e) {
        const i18n = vm?.proxy as unknown as I18nProxy
        openToast((i18n?.$t && i18n.$t('integrations.toastSaveFailed')), 'is-danger')
      } finally {
        saving.value = false
      }
    }

    const jsonEditorMode: Mode = Mode.text

    // Two-way computed IntegrationInitial for JSON tab
    const integrationInitial = computed<IntegrationInitial>({
      get() {
        const queryObj = form.queryParams
          .filter(p => p.key || p.value)
          .reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {} as Record<string, string>)
        const headerObj = form.headers
          .filter(p => p.key || p.value)
          .reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {} as Record<string, string>)
        const result: IntegrationInitial = {
          name: form.name,
          url: form.url,
          requestType: form.requestType,
          responseTemplate: form.responseTemplate
        }
        if (form.deviceId && form.deviceId !== '*') {
          (result as any).deviceID = form.deviceId
        }
        if (!form.enabled) {
          result.disabled = true
        }
        if (form.requestType === 'GET') {
          result.query = queryObj
        }
        if (form.requestType !== 'GET' && Object.keys(queryObj).length > 0) {
          result.query = queryObj
        }
        if (Object.keys(headerObj).length > 0) {
          (result as any).headers = headerObj
        }
        if (form.parameters && form.parameters.length > 0) {
          (result as any).parameters = form.parameters.map(p => ({ template_name: p.template_name, value: p.value }))
        }
        if (form.basicAuth.username || form.basicAuth.password) {
          (result as any).auth = {
            username: form.basicAuth.username || '',
            password: form.basicAuth.password || ''
          }
        }
        if (form.requestFormat === 'form') {
          result.form = form.formParams
            .filter(p => p.key || p.value)
            .reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {} as Record<string, string>)
        } else if (form.requestFormat === 'json') {
          result.json = form.jsonData || {}
        } else if (form.requestFormat === 'custom') {
          if (form.body) result.body = form.body
        }
        if (form.noDefaults) {
          result.noDefaults = true as any
        }
        if (form.secrets && form.secrets.length > 0) {
          (result as any).secrets = form.secrets
        } else if (selectedSecrets.value.length > 0) {
          (result as any).secrets = selectedSecrets.value.map(n => ({ name: n }))
        }
        if (form.template !== 'webhook') {
          result.template = form.template
        }
        return result
      },
      set(val) {
        if (!val) return
        const v = val as IntegrationInitial
        form.name = (v.name as string) || ''
        form.url = (v.url as string) || ''
        form.requestType = (v.requestType as string) || form.requestType
        // map deviceID -> deviceId selection
        if (v.deviceID) {
          form.deviceId = String(v.deviceID)
        } else {
          form.deviceId = '*'
        }
        if (typeof v.disabled === 'boolean') {
          form.enabled = !v.disabled
        } else {
          form.enabled = true
        }
        // Determine request format from object fields
        if (form.requestType === 'GET') {
          form.requestFormat = 'query'
        } else if (v.form) {
          form.requestFormat = 'form'
        } else if (v.json) {
          form.requestFormat = 'json'
        } else if (v.body) {
          form.requestFormat = 'custom'
        }
        const q: Record<string, unknown> = v.query ?? {}
        form.queryParams = Object.keys(q).length
          ? Object.keys(q).map(k => ({ key: k, value: String((q as Record<string, unknown>)[k] ?? '') }))
          : [{ key: '', value: '' }]
        const f: Record<string, unknown> = v.form ?? {}
        form.formParams = Object.keys(f).length
          ? Object.keys(f).map(k => ({ key: k, value: String((f as Record<string, unknown>)[k] ?? '') }))
          : [{ key: '', value: '' }]
        form.jsonData = v.json || {}
        form.body = (v.body as string) || ''
        form.noDefaults = v.noDefaults === true
        const h: Record<string, unknown> = v.headers ?? {}
        form.headers = Object.keys(h).length
          ? Object.keys(h).map(k => ({ key: k, value: String((h as Record<string, unknown>)[k] ?? '') }))
          : [{ key: '', value: '' }]
        const paramsArr: any[] = v.parameters || []
        form.parameters = Array.isArray(paramsArr)
          ? paramsArr.map((p: any) => ({ template_name: String(p?.template_name || ''), value: String(p?.value || '') })).filter(p => p.template_name)
          : []
        const au: any = v.auth || {}
        form.basicAuth.username = au.username || ''
        form.basicAuth.password = au.password || ''
        form.responseTemplate = (v.responseTemplate as string) || ''
        // secrets from JSON
        const secretsArr: any[] = v.secrets || []
        form.secrets = Array.isArray(secretsArr)
          ? secretsArr.map((s: any) => ({ template_name: s?.template_name, name: s?.name }))
          : []
        form.template = v.template || 'webhook'
        selectedSecrets.value = Array.isArray(secretsArr) ? secretsArr.map((s: any) => String(s?.name || '')).filter(Boolean) : []
      }
    })

    const activeMainTab = ref<'form' | 'json'>('form')
    
    // Basic/Advanced view toggle
    const isAdvancedView = ref(false)
    const toggleView = () => {
      isAdvancedView.value = !isAdvancedView.value
    }
    
    // Visibility logic
    const isVisible = (elementKey: string): boolean => {
      if (isAdvancedView.value) {
        return true
      }

      const visibilityConfig = props.visibilityConfig || {
        jsonTab: true,
        requestType: true,
        requestFormat: true,
        queryParams: false,
        httpHeaders: false,
        httpBasicAuth: false,
        responseTemplate: false
      }

      // If element is explicitly set to false in config, hide it
      if (visibilityConfig[elementKey] === false) return false
      
      return true
    }

    // Sync tabs with requestFormat
    const activeRequestFormatTab = ref<string | null>(form.requestFormat)
    const defaultsSelection = ref<'default' | 'custom'>(form.noDefaults ? 'custom' : 'default')
    watch(defaultsSelection, (val) => {
      form.noDefaults = (val === 'custom')
    })
    watch(() => form.noDefaults, (val) => {
      defaultsSelection.value = val ? 'custom' : 'default'
    })

    const defaultFormPairs = computed(() => [
      { key: 'data', value: '{{{FERMION_EVENT_VALUE}}}' },
      { key: 'coreid', value: '{{{FERMION_DEVICE_ID}}}' },
      { key: 'published_at', value: '{{{FERMION_PUBLISHED_AT}}}' }
    ])

    const defaultJsonObject = computed<Record<string, unknown>>(() => ({
      data: '{{{FERMION_EVENT_VALUE}}}',
      coreid: '{{{FERMION_DEVICE_ID}}}',
      published_at: '{{{FERMION_PUBLISHED_AT}}}'
    }))

    const defaultJsonHeaders = computed(() => ([
      { key: 'Content-Type', value: 'application/json' },
      { key: 'Accept', value: 'application/json' }
    ]))

    const defaultFormHeaders = computed(() => ([
      { key: 'Content-Type', value: 'application/x-www-form-urlencoded' }
    ]))

    watch(() => form.requestFormat, (fmt) => {
      if (fmt === 'form' || fmt === 'json' || fmt === 'custom') {
        activeRequestFormatTab.value = fmt
      }
    })
    watch(activeRequestFormatTab, (tab) => {
      if (form.requestType === 'GET') return
      if (tab === 'form' || tab === 'json' || tab === 'custom') {
        form.requestFormat = tab
      }
    })

    onMounted(async () => {
      await store.dispatch('loadModel', { model: 'secrets' })
    })

    // Slot helpers for secrets mapping
    const getParam = (name: string): string => {
      const found = Array.isArray((form as any).parameters)
        ? (form as any).parameters.find((p: any) => p?.template_name === name)
        : null
      const val = String(found?.value ?? '')
      return val
    }
    const setParam = (name: string, value: string) => {
      if (!Array.isArray((form as any).parameters)) {
        (form as any).parameters = []
      }
      const idx = (form as any).parameters.findIndex((p: any) => p?.template_name === name)
      if (idx >= 0) {
        (form as any).parameters[idx].value = value
      } else {
        (form as any).parameters.push({ template_name: name, value })
      }
    }
    const getSecret = (templateName: string): string => {
      const found = Array.isArray((form as any).secrets)
        ? (form as any).secrets.find((s: any) => s?.template_name === templateName)
        : null
      const name = String(found?.name || '')
      return name
    }
    const setSecret = (templateName: string, secretName: string) => {
      if (!Array.isArray((form as any).secrets)) {
        (form as any).secrets = []
      }
      const idx = (form as any).secrets.findIndex((s: any) => s?.template_name === templateName)
      if (idx >= 0) {
        (form as any).secrets[idx].name = secretName
        if ((form as any).secrets[idx].value) delete (form as any).secrets[idx].value
      } else {
        (form as any).secrets.push({ template_name: templateName, name: secretName })
      }
      if (secretName && selectedSecrets?.value && !selectedSecrets.value.includes(secretName)) {
        selectedSecrets.value.push(secretName)
      }
    }

    return { form, deviceList, save, saving, jsonEditorMode, activeRequestFormatTab, activeMainTab, integrationInitial, defaultsSelection, defaultFormPairs, defaultJsonObject, defaultJsonHeaders, defaultFormHeaders, selectedSecrets, secretModalOpen, filteredSecretOptions, onSecretTyping, handleSecretCreated, secretFormInModal, canSaveSecret, getSecret, setSecret, getParam, setParam, isAdvancedView, toggleView, isVisible, isValid, nameInput, urlInput }
  }
})
</script>
